// 회원 등록/편집/초대 시트 묶음 — 운영진(주로 회장)이 회원 마스터를
// 관리할 때 여는 시트들이 한 도메인이라 같은 파일.
//
//  - openNewMemberSheet / openEditMemberSheet: 단건 추가/편집 시트
//  - openBulkRegisterSheet: 줄바꿈 구분된 이름 목록 + 공용 초대 링크 발급
//  - MemberForm: 두 단건 시트의 공용 폼(이름·성별·레벨·NTRP·권한·상태·연락처)
//  - issueMemberInvite + InviteResultSheet: 단건 회원 초대 링크 발급/공유
//  - RosterInviteResultSheet: 명단 일괄 등록 후 공용 링크 결과 시트
//
// 저장/삭제/발급 후 화면 리프레시는 opts.rerender 콜백으로 호출자에 위임.

import { Fragment } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { api } from "../api";
import { isAdmin, isPresident, levelOptionsForGender } from "../auth";
import { parseBulkNames } from "./index";
import { invalidateMembersCache, SHARE_ORIGIN, state } from "../state";
import { Icon } from "../ui/components/Icon";
import { Segment } from "../ui/components/Segment";
import { attachPhoneFormatter } from "../ui/format";
import { mountPreact } from "../ui/preact-mount";
import { closeSheet, openSheet, toast, withBusy } from "../ui/sheet";

export interface MemberFormOpts {
  rerender: () => void;
}

// ---- shared clipboard helpers ----
function fallbackCopy(text: string): boolean {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.focus(); ta.select();
  let ok = false;
  try { ok = document.execCommand("copy"); } catch { ok = false; }
  document.body.removeChild(ta);
  return ok;
}

async function copy(text: string) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      toast("복사했어요");
      return;
    }
  } catch {}
  if (fallbackCopy(text)) toast("복사했어요"); else toast("복사에 실패했어요");
}

// =====================================================================
// 단건 회원 폼 (신규/편집)
// =====================================================================

const NTRP_MIN = 1.0;
const NTRP_MAX = 7.0;
const NTRP_STEP = 0.5;
const clampNtrp = (v: number) => Math.max(NTRP_MIN, Math.min(NTRP_MAX, Math.round(v * 2) / 2));

interface MemberFormProps {
  existing: any;
  existingMembers: any[] | undefined;
  rerender: () => void;
}

function MemberForm({ existing, existingMembers, rerender }: MemberFormProps) {
  const [name, setName] = useState<string>(existing?.name || "");
  const [gender, setGender] = useState<"male" | "female">(existing?.gender === "female" ? "female" : "male");
  const [levelCat, setLevelCat] = useState<string | null>(existing?.level_category || null);
  const [ntrp, setNtrp] = useState<number>(clampNtrp(existing?.ntrp != null ? Number(existing.ntrp) : 2.5));
  const existingRole = existing?.app_role || "participant";
  const existingIsPresident = existingRole === "president";
  const existingIsAdmin = existingIsPresident
    || existingRole === "admin"
    || existingRole === "staff"
    || existingRole === "platform_admin";
  const [role, setRole] = useState<"participant" | "admin">(
    existingIsPresident ? "admin" : (existingIsAdmin ? "admin" : "participant"),
  );
  const [status, setStatus] = useState<"active" | "on_leave" | "excluded">(
    (existing?.member_status as any) || "active",
  );

  const phoneRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (phoneRef.current) attachPhoneFormatter(phoneRef.current); }, []);

  const isSelf = !!(existing?.member_id && existing.member_id === state.session?.actor?.member_id);
  const canEditRole = isPresident() && !existingIsPresident;
  const levelOpts = levelOptionsForGender(gender);

  function onGenderChange(val: "male" | "female") {
    setGender(val);
    const opts = levelOptionsForGender(val);
    if (levelCat && !opts.includes(levelCat)) setLevelCat(null);
  }

  async function onSubmit(e: Event) {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) { toast("이름을 입력해주세요"); return; }
    if (!existing && Array.isArray(existingMembers)) {
      const dup = existingMembers.find((m: any) =>
        (m.name || "").trim() === trimmedName && (m.member_status || "active") === "active",
      );
      if (dup) {
        const msg =
          `같은 이름의 활성 회원이 이미 있어요: "${trimmedName}"\n\n` +
          `이대로 추가하면 두 분 중 한 분은 본인 PIN으로 로그인할 수 없어요 (로그인이 이름 기준).\n\n` +
          `구분자를 붙이길 권장해요. 예) "${trimmedName}A", "${trimmedName}B"\n\n` +
          `정말 이 이름 그대로 추가할까요?`;
        if (!confirm(msg)) return;
      }
    }
    const phone = phoneRef.current?.value.trim() || "";
    const body: any = {
      name: trimmedName,
      gender,
      ntrp,
      level_category: levelCat || null,
      phone_or_contact: phone || null,
    };
    if (isAdmin()) {
      if (existingIsPresident) body.app_role = "president";
      else if (canEditRole || !existing) body.app_role = role === "admin" ? "staff" : "participant";
      if (existing) body.member_status = status;
    }
    try {
      if (existing) {
        await api(`/api/v1/members/${existing.member_id}`, { method: "PATCH", body });
        toast("회원 정보를 수정했어요");
      } else {
        await api("/api/v1/members", { method: "POST", body });
        toast("새 회원을 추가했어요");
      }
      invalidateMembersCache();
      closeSheet();
      rerender();
    } catch (err: any) { toast(err.message); }
  }

  async function onDelete() {
    if (!confirm(`${existing.name}님을 삭제할까요? 과거 일정 기록은 그대로 남습니다.`)) return;
    try {
      await api(`/api/v1/members/${existing.member_id}`, { method: "DELETE" });
      invalidateMembersCache();
      toast("회원을 삭제했어요");
      closeSheet();
      rerender();
    } catch (err: any) { toast(err.message); }
  }

  const roleHint = existingIsPresident
    ? "회장 권한은 페어매치팀에 문의해야 변경돼요."
    : !canEditRole
      ? "운영진 임명·해제는 회장만 할 수 있어요."
      : isSelf && existingIsAdmin
        ? "본인의 운영진 권한은 스스로 해제할 수 없어요."
        : "운영진은 다른 회원을 초대·편집하고 일정을 만들 수 있어요.";

  const roleSegDisabled = existingIsPresident || !canEditRole;
  const roleSegSelfLockParticipant = isSelf && existingIsAdmin;

  return (
    <form class="form" onSubmit={onSubmit}>
      <div class="field">
        <span class="field__label">이름</span>
        <input
          class="input"
          type="text"
          value={name}
          required
          onInput={(e) => setName((e.currentTarget as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <span class="field__label">성별</span>
        <Segment
          options={[["male", "남"], ["female", "여"]]}
          initial={gender}
          onChange={onGenderChange}
        />
      </div>
      <div class="field">
        <span class="field__label">레벨 (선택)</span>
        <div class="chip-row">
          {levelOpts.map((opt) => (
            <button
              key={opt}
              type="button"
              class={"chip" + (opt === levelCat ? " chip--selected" : "")}
              onClick={() => setLevelCat(levelCat === opt ? null : opt)}
            >
              {opt}
            </button>
          ))}
        </div>
        <span class="field__hint">회원에게 공개되는 레벨 카테고리예요.</span>
      </div>
      <div class="field">
        <span class="field__label">NTRP (운영진 전용)</span>
        <div class="stepper">
          <button
            type="button"
            class="stepper__btn"
            aria-label="감소"
            onClick={() => setNtrp((v) => clampNtrp(v - NTRP_STEP))}
          >
            <Icon name="minus" size={18} />
          </button>
          <div class="stepper__value">{ntrp.toFixed(1)}</div>
          <button
            type="button"
            class="stepper__btn"
            aria-label="증가"
            onClick={() => setNtrp((v) => clampNtrp(v + NTRP_STEP))}
          >
            <Icon name="plus" size={18} />
          </button>
        </div>
        <span class="field__hint">NTRP는 운영진만 볼 수 있어요.</span>
      </div>
      {isAdmin() && (
        <div class="field">
          <span class="field__label">권한</span>
          <RoleSegment
            value={existingIsPresident ? "admin" : role}
            disabled={roleSegDisabled}
            disabledTitle={
              existingIsPresident ? "회장 권한은 페어매치팀에서 변경해요"
              : !canEditRole ? "운영진 권한 변경은 회장만 할 수 있어요"
              : undefined
            }
            participantLocked={roleSegSelfLockParticipant && !roleSegDisabled}
            onChange={setRole}
          />
          <span class="field__hint">{roleHint}</span>
        </div>
      )}
      {isAdmin() && existing && (
        <div class="field">
          <span class="field__label">상태</span>
          <Segment
            options={[["active", "활성"], ["on_leave", "휴회"], ["excluded", "제외"]]}
            initial={status}
            onChange={(v: "active" | "on_leave" | "excluded") => setStatus(v)}
          />
          <span class="field__hint">휴회·제외 회원은 회원 목록과 일정 참가자 후보에서 제외돼요.</span>
        </div>
      )}
      <div class="field">
        <span class="field__label">연락처 (선택)</span>
        <input
          ref={phoneRef}
          class="input"
          type="tel"
          inputmode="tel"
          value={existing?.phone_or_contact || ""}
          placeholder="번호만 입력해주세요 (선택)"
        />
        <span class="field__hint">앱에서는 알림을 보내지 않아요. 운영진 내부 연락용이에요.</span>
      </div>
      <button class="btn btn--primary btn--block" type="submit">
        {existing ? "저장" : "회원 추가"}
      </button>
      {existing && (
        <Fragment>
          <div class="section" style="margin-top:16px">
            <div class="section__title"><h3>로그인 초대</h3></div>
            <p class="field__hint">
              초대 링크를 발급하면 회원이 본인 PIN을 설정하고 앱을 쓸 수 있어요. 재발급 시 기존 PIN은 무효화돼요.
            </p>
            <button
              class="btn btn--secondary btn--block"
              type="button"
              style="margin-top:8px"
              onClick={(e) => issueMemberInvite(existing, e.currentTarget as HTMLButtonElement)}
            >
              <Icon name="link" size={16} />
              초대 링크 발급
            </button>
          </div>
          <button
            class="btn btn--danger btn--block"
            type="button"
            style="margin-top:8px"
            onClick={onDelete}
          >
            회원 삭제
          </button>
        </Fragment>
      )}
    </form>
  );
}

function RoleSegment({
  value,
  disabled,
  disabledTitle,
  participantLocked,
  onChange,
}: {
  value: "participant" | "admin";
  disabled: boolean;
  disabledTitle?: string;
  participantLocked: boolean;
  onChange: (v: "participant" | "admin") => void;
}) {
  return (
    <div class="segment">
      {(["participant", "admin"] as const).map((key) => {
        const label = key === "participant" ? "일반 회원" : "운영진";
        const btnDisabled = disabled || (participantLocked && key === "participant");
        const title = disabled
          ? disabledTitle
          : (participantLocked && key === "participant" ? "본인 권한은 스스로 해제할 수 없어요" : undefined);
        return (
          <button
            key={key}
            type="button"
            class={"segment__btn" + (value === key ? " segment__btn--active" : "")}
            disabled={btnDisabled}
            title={title}
            onClick={() => onChange(key)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

export function openNewMemberSheet(existingMembers: any[], opts: MemberFormOpts): void {
  openSheet("새 회원", mountPreact(
    <MemberForm existing={null} existingMembers={existingMembers} rerender={opts.rerender} />,
  ));
}

export function openEditMemberSheet(m: any, opts: MemberFormOpts): void {
  openSheet("회원 편집", mountPreact(
    <MemberForm existing={m} existingMembers={undefined} rerender={opts.rerender} />,
  ));
}

// =====================================================================
// 명단 일괄 등록 시트
// =====================================================================

function BulkRegisterForm({
  existingMembers,
  rerender,
}: {
  existingMembers: any[];
  rerender: () => void;
}) {
  const pending = useMemo(
    () => (existingMembers || []).filter((m: any) => !m.pin_set && (m.member_status || "active") === "active"),
    [existingMembers],
  );
  const pendingNames = useMemo(() => pending.map((m: any) => m.name).filter(Boolean), [pending]);

  const [namesText, setNamesText] = useState("");
  const submitRef = useRef<HTMLButtonElement>(null);

  const newNames = parseBulkNames(namesText);
  const total = pending.length + newNames.length;

  async function onSubmit(e: Event) {
    e.preventDefault();
    if (newNames.length === 0 && pending.length === 0) {
      toast("등록할 이름을 입력하거나 미가입 회원이 있어야 해요");
      return;
    }
    if (newNames.length > 0) {
      const activeExistingNames = new Set(
        (existingMembers || [])
          .filter((m: any) => (m.member_status || "active") === "active")
          .map((m: any) => (m.name || "").trim())
          .filter(Boolean),
      );
      const seen = new Set<string>();
      const dupAgainstExisting: string[] = [];
      const dupWithinNew: string[] = [];
      for (const n of newNames) {
        if (activeExistingNames.has(n) && !dupAgainstExisting.includes(n)) dupAgainstExisting.push(n);
        if (seen.has(n) && !dupWithinNew.includes(n)) dupWithinNew.push(n);
        seen.add(n);
      }
      if (dupAgainstExisting.length > 0 || dupWithinNew.length > 0) {
        const parts = ["동명이인이 감지됐어요.\n"];
        if (dupAgainstExisting.length > 0) parts.push(`• 기존 회원과 같은 이름: ${dupAgainstExisting.join(", ")}`);
        if (dupWithinNew.length > 0) parts.push(`• 이번 입력에서 중복: ${dupWithinNew.join(", ")}`);
        parts.push(
          "\n이대로 등록하면 같은 이름을 가진 분 중 한 명만 본인 PIN으로 로그인할 수 있어요 " +
          "(로그인이 이름 기준).\n\n" +
          "구분자를 붙이길 권장해요. 예) \"김민수A\", \"김민수B\"\n\n" +
          "그래도 이대로 등록할까요?",
        );
        if (!confirm(parts.join("\n"))) return;
      }
    }
    try {
      await withBusy(submitRef.current, "발급 중…", async () => {
        if (newNames.length > 0) {
          await api("/api/v1/members/bulk", { method: "POST", body: { names: newNames } });
          invalidateMembersCache();
        }
        const slug = state.config.clubSlug;
        const res = await api(`/api/v1/clubs/${slug}/roster-invite`, { method: "POST", body: {} });
        closeSheet();
        showRosterInviteResultSheet(res, newNames.length + pending.length);
        if (state.route.name === "members") rerender();
      });
    } catch (err: any) {
      toast(err.message || "요청에 실패했어요");
    }
  }

  return (
    <form class="form" onSubmit={onSubmit}>
      <p class="muted small" style="margin:0 0 4px">
        한 번에 여러 명을 등록하고 공용 초대 링크 하나로 모두 가입시켜요. 이름만 있으면 돼요.
      </p>
      <div class="field">
        <span class="field__label">이름 목록</span>
        <textarea
          class="input"
          rows={8}
          placeholder={"한 줄에 한 명씩\n예)\n김민수\n이지훈\n박하늘"}
          style="resize:vertical;min-height:140px;font-family:inherit"
          value={namesText}
          onInput={(e) => setNamesText((e.currentTarget as HTMLTextAreaElement).value)}
        />
        <span class="field__hint">
          줄바꿈·쉼표·탭으로 구분해요. 붙여넣기 가능. 동명이인은 "김민수A / 김민수B" 처럼 구분 힌트를 붙여주세요.
        </span>
      </div>
      <div class="field">
        <span class="field__label">미리보기</span>
        <div class="card" style="padding:10px 12px">
          <p class="card__lead" style="margin:0 0 6px">
            {total === 0 ? "등록할 이름이 없어요" : `초대 대상 ${total}명`}
          </p>
          {newNames.length > 0 && (
            <Fragment>
              <p class="muted small" style="margin:6px 0 2px">신규 등록: {newNames.length}명</p>
              <p class="small" style="margin:0;word-break:keep-all;line-height:1.5">{newNames.join(", ")}</p>
            </Fragment>
          )}
          {pendingNames.length > 0 && (
            <Fragment>
              <p class="muted small" style="margin:8px 0 2px">
                기존 미가입: {pendingNames.length}명 (같은 링크로 가입 가능)
              </p>
              <p class="small" style="margin:0;word-break:keep-all;line-height:1.5">{pendingNames.join(", ")}</p>
            </Fragment>
          )}
        </div>
      </div>
      <p class="muted small" style="margin:0">
        성별/레벨은 각자 가입할 때 본인이 선택해요. 48시간 유효한 공용 링크가 발급됩니다.
      </p>
      <button ref={submitRef} class="btn btn--primary btn--block" type="submit">
        등록 후 공용 초대 링크 발급
      </button>
    </form>
  );
}

export function openBulkRegisterSheet(existingMembers: any[], opts: MemberFormOpts): void {
  openSheet("명단 일괄 등록 + 초대", mountPreact(
    <BulkRegisterForm existingMembers={existingMembers} rerender={opts.rerender} />,
  ));
}

// =====================================================================
// 초대 링크 결과 시트
// =====================================================================

function InviteResultBody({
  url,
  shareMessage,
  expires,
  totalCount,
  variant,
}: {
  url: string;
  shareMessage: string;
  expires: string;
  totalCount: number | null;
  variant: "single" | "roster";
}) {
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const lead = variant === "roster"
    ? (totalCount && totalCount > 0
        ? `${totalCount}명에게 보낼 공용 초대 링크를 발급했어요.`
        : "공용 초대 링크를 발급했어요.")
    : "발급했어요. 카톡 DM으로 전달해주세요.";
  const fieldLabel = variant === "roster" ? "공용 초대 링크" : "초대 링크";
  const tail = variant === "roster"
    ? "링크 공유 시 각자 본인 이름을 선택해 PIN을 설정해요. 새 링크를 발급하면 이전 링크는 무효화돼요."
    : "회원이 링크를 열면 이름을 확인하고 본인 PIN을 직접 설정해요.";
  return (
    <div class="stack">
      <div class="card">
        <p class="card__lead">{lead}</p>
        <p class="card__sub">유효 기간: {expires} (발급 후 48시간)</p>
      </div>
      <div class="field">
        <span class="field__label">{fieldLabel}</span>
        <div class="invite-copy">
          <input
            class="input"
            type="text"
            value={url}
            readonly
            onClick={(e) => (e.currentTarget as HTMLInputElement).select()}
          />
          <button class="btn btn--secondary btn--sm" type="button" onClick={() => copy(url)}>복사</button>
        </div>
      </div>
      {canShare ? (
        <button
          class="btn btn--primary btn--block"
          type="button"
          onClick={async () => {
            try {
              await (navigator as any).share({ text: shareMessage });
              // 공유 성공 = 작업 완료라 시트 자동 닫기. 사용자가 취소하면 reject되어
              // catch로 들어가 시트는 그대로 유지(다시 시도 가능).
              closeSheet();
            } catch {}
          }}
        >
          <Icon name="link" size={16} />
          카톡/메시지로 공유
        </button>
      ) : (
        <button
          class="btn btn--primary btn--block"
          type="button"
          onClick={() => copy(shareMessage)}
        >
          <Icon name="link" size={16} />
          안내 문구 전체 복사
        </button>
      )}
      <p class={variant === "roster" ? "muted small" : "field__hint"} style={variant === "roster" ? "margin-top:4px" : undefined}>
        {tail}
      </p>
    </div>
  );
}

function showRosterInviteResultSheet(invite: any, totalCount: number): void {
  const slug = state.config.clubSlug;
  const url = `${SHARE_ORIGIN}/c/${slug}/roster-join/${invite.roster_token}`;
  const expires = invite.expires_at ? new Date(invite.expires_at).toLocaleString("ko-KR") : "";
  const clubName = state.club?.name || invite.club_name || "테니스 클럽";
  const shareMessage =
    `페어매치 앱 초대\n` +
    `${clubName}의 모임·대진·경기 기록을 앱에서 함께 관리해요.\n\n` +
    `① 아래 링크 접속\n` +
    `② 본인 이름 선택\n` +
    `③ PIN 6자리 설정 → 가입 완료\n\n` +
    `${url}\n\n` +
    `(유효 기간: ${expires} 까지)`;
  openSheet("공용 초대 링크", mountPreact(
    <InviteResultBody url={url} shareMessage={shareMessage} expires={expires} totalCount={totalCount} variant="roster" />,
  ));
}

function showInviteResultSheet(invite: any): void {
  const slug = state.config.clubSlug;
  const url = `${SHARE_ORIGIN}/c/${slug}/invite/${invite.member_id}/${invite.invite_token}`;
  const expires = invite.expires_at ? new Date(invite.expires_at).toLocaleString("ko-KR") : "";
  const clubName = state.club?.name || "테니스 클럽";
  const shareMessage =
    `페어매치 앱 초대\n` +
    `${clubName}에서 ${invite.name}님을 초대합니다. 모임·대진·경기 기록을 페어매치에서 함께 관리해요.\n\n` +
    `① 아래 링크 접속\n` +
    `② PIN 6자리 설정 → 가입 완료\n\n` +
    `${url}\n\n` +
    `(유효 기간: ${expires} 까지)`;
  openSheet("초대 링크", mountPreact(
    <InviteResultBody url={url} shareMessage={shareMessage} expires={expires} totalCount={null} variant="single" />,
  ));
}

async function issueMemberInvite(member: any, btn: HTMLButtonElement): Promise<void> {
  try {
    const res = await withBusy(btn, "발급 중…", () =>
      api(`/api/v1/members/${member.member_id}/invite`, { method: "POST", body: {} }),
    );
    showInviteResultSheet(res);
  } catch (err: any) {
    toast(err.message || "발급에 실패했어요");
  }
}
